/**
 * Safe, same-origin src/href for a media reference.
 *
 * Two jobs.
 *
 * **Mixed content.** The console is served over https; MinIO is plain http on
 * :9000. A browser silently blocks an `http://…` image or video on an https
 * page, so the asset never renders. Rewriting to `/media/…` lets the frontend
 * nginx proxy it over the page's own origin (see apps/admin-web/nginx.conf).
 *
 * **Stored XSS.** KYC document links render seller- and partner-supplied values
 * as `<a href={…}>`. The API previously validated those with `z.string().url()`,
 * which accepts `javascript:` and `data:` — React 18 only warns on such an href,
 * it does not block it, so a hostile seller could take over an admin session
 * from the KYC review queue. The API now allowlists the scheme; this is the
 * second layer, at the sink, because that is the right shape for an XSS sink.
 *
 * Anything not recognisably safe returns '' rather than being passed through.
 */
export function mediaSrc(url: string | null | undefined): string {
  if (!url) return '';
  const value = url.trim();
  if (value === '') return '';

  // Our own object storage → same-origin proxy path.
  const i = value.indexOf('/media/');
  if (i >= 0) return stripTraversal(value.slice(i));
  // A bare key from the API when S3_PUBLIC_BASE_URL is unset. Without the
  // leading slash the browser resolves it against the current route.
  if (value.startsWith('media/')) return stripTraversal(`/${value}`);

  // Anything else must be an explicitly safe absolute URL. This is the branch
  // that refuses javascript:, data:, file: and friends.
  try {
    const parsed = new URL(value, window.location.origin);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? value : '';
  } catch {
    return '';
  }
}

/** MinIO is path-style, so `..` in a key escapes into another bucket. */
function stripTraversal(path: string): string {
  return path.split('/').some((s) => s === '.' || s === '..') ? '' : path;
}

/**
 * href for a KYC document link.
 *
 * The API returns identity documents as short-lived signed URLs (they are not
 * public). A signature covers the exact host and path it was issued for, so —
 * unlike [mediaSrc] — the URL must NOT be rewritten onto the `/media/` proxy;
 * it is used as-is, provided it is http(s). Anything unsigned falls back to
 * [mediaSrc] (external legacy links, or an API without storage configured).
 */
export function documentHref(url: string | null | undefined): string {
  if (!url) return '';
  const value = url.trim();
  if (/^https?:\/\//i.test(value) && /[?&]X-Amz-Signature=/.test(value)) {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? value : '';
    } catch {
      return '';
    }
  }
  return mediaSrc(value);
}
